import { TextField, Grid, MenuItem, Link, FormControlLabel, Checkbox, Box } from '@mui/material';
import { getControlId } from '@veeva-ctv/utils';
import { FormikProps } from 'formik';
import { SiteApplicationInput } from '@/gql/graphql';
import AMCForm from './SiteDetailsFormInners/AMCHospitalForm';
import PrivatePracticeForm from './SiteDetailsFormInners/PrivatePractiveForm';
import SMOForm from './SiteDetailsFormInners/SMOForm';
import useURLsContext from '@/context/URLsContext';
import { UIOnlyFields, ResearchSites } from '@/types';
import { researchSitesValues } from '@/constants/formInitConstants';
import {
  REGULAR_FLOW_SCREEN_NAMES,
  INVITE_FLOW_SCREEN_NAMES,
} from '@/constants/eventTrackingConstants';
import { useEffect } from 'react';
import { push } from '@/utils/matomoUtils';

interface SiteDetailsFormProps {
  formik: FormikProps<UIOnlyFields & SiteApplicationInput>;
  isInviteFlow?: boolean;
}

const SiteDetailsForm = ({ formik, isInviteFlow = false }: SiteDetailsFormProps) => {
  useEffect(() => {
    if (isInviteFlow) {
      push(['trackPageView', INVITE_FLOW_SCREEN_NAMES.siteDetails]);
    } else {
      push(['trackPageView', REGULAR_FLOW_SCREEN_NAMES.siteDetails]);
    }
  }, []);

  const resetStepFormFields = {
    name: '',
    organization: '',
    address1: '',
    country: '',
    city: '',
    stateProvince: '',
    postalCode: '',
    url: '',
    hasMultiLocations: undefined,
    isUniqueSite: undefined,
    smoAddress1: '',
    smoCountry: '',
    smoCity: '',
    smoState: '',
    smoZip: '',
    addressAutocompleteLabel: '',
  };

  const siteTypesOptions = Object.values(ResearchSites);

  const urls = useURLsContext();
  return (
    <Grid container rowSpacing={1}>
      <Grid item xs={12}>
        <TextField
          id={getControlId({
            type: 'select',
            name: 'siteType',
          })}
          name="siteType"
          fullWidth
          select
          label="What type of research site are you signing up?*"
          value={formik.values.siteType}
          onBlur={formik.handleBlur}
          onChange={(e) => {
            formik.resetForm({ values: { ...formik.values, ...resetStepFormFields } });
            formik.handleChange(e);
            void formik.setTouched({});
          }}>
          {siteTypesOptions.map((elem, idx) => (
            <MenuItem key={idx} value={researchSitesValues[elem]} sx={{ whiteSpace: 'normal' }}>
              {elem}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {(formik.values.siteType === researchSitesValues[ResearchSites.SMA] ||
        formik.values.siteType === researchSitesValues[ResearchSites.HOSPITAL]) && (
        <AMCForm formik={formik} />
      )}

      {formik.values.siteType === researchSitesValues[ResearchSites.PRIVATE_PRACTICE] && (
        <PrivatePracticeForm formik={formik} />
      )}

      {formik.values.siteType === researchSitesValues[ResearchSites.SMO] && (
        <SMOForm formik={formik} />
      )}

      {isInviteFlow && formik.values.siteType && (
        <Grid item xs={12}>
          <Box mt={-1} mb={1} ml={0.2}>
            <FormControlLabel
              control={
                <Checkbox
                  id={getControlId({ type: 'checkbox', name: 'agreedToTOS' })}
                  name="agreedToTOS"
                  checked={formik.values.agreedToTOS}
                  onChange={(e) =>
                    void formik.setFieldValue('agreedToTOS', e.target.checked)
                  }></Checkbox>
              }
              label={
                <>
                  I agree to the Veeva SiteVault{' '}
                  <Link
                    href={urls?.TOSUrl ? urls.TOSUrl : process.env.NEXT_PUBLIC_TERMS_EXTERNAL_URL}
                    color="primary"
                    target="_blank">
                    Terms of Service
                  </Link>
                  .*
                </>
              }
            />
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default SiteDetailsForm;
